import type { SetStateAction } from 'react';
import type { ZodSchema } from 'zod';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export interface UseStorageOptions<T> {
  /**
   * How often (in ms) to poll storage for external changes.
   */
  poll?: number;
  logError?: (error: any) => void;
  schema?: ZodSchema<T>;
  /**
   * If true, updates the stored value when initialValue changes,
   * but only if the current stored value still matches the previous initialValue.
   * Set to avoid overwriting user modified values.
   * @default false
   */
  updateInitialValueOnChange?: boolean;
}

export function getStorage<T>(
  storage: typeof window.localStorage | typeof window.sessionStorage,
  key: string,
  initialValue: T,
  { logError, schema }: UseStorageOptions<T> = {},
): T {
  try {
    const saved = storage.getItem(key);
    if (saved) {
      const parsed = JSON.parse(saved);

      if (
        parsed &&
        typeof parsed === 'object' &&
        'meterExpiry' in parsed &&
        typeof parsed.meterExpiry === 'number' &&
        parsed.meterExpiry < Date.now()
      ) {
        storage.removeItem(key);
        return initialValue;
      }

      const parsedData =
        parsed && typeof parsed === 'object' && 'meterData' in parsed ? parsed.meterData : parsed;

      if (!schema) {
        return parsedData;
      }

      return schema.parse(parsedData);
    }
  } catch (err) {
    logError?.(err);
  }

  return initialValue;
}

export interface SetStorageOptions {
  /**
   * Optional expiry timestamp (in epoch ms).
   * If provided, the stored value will be automatically cleared after this time.
   */
  expiry?: number;
}

type SetValueWithOptions<T> = (value: SetStateAction<T>, options?: SetStorageOptions) => void;

export function useStorage<T>(
  storage: typeof window.localStorage | typeof window.sessionStorage,
  key: string,
  initialValue: T,
  { poll, logError, schema, updateInitialValueOnChange }: UseStorageOptions<T> = {},
): [T, SetValueWithOptions<T>, () => void] {
  const [storedValue, setStoredValue] = useState<T>(() =>
    getStorage(storage, key, initialValue, { logError, schema }),
  );

  const setValue: SetValueWithOptions<T> = useCallback(
    (value, options) => {
      try {
        setStoredValue((prev) => {
          const valueToStore = value instanceof Function ? value(prev) : value;

          if (prev !== valueToStore) {
            const payload = options?.expiry
              ? { meterData: valueToStore, meterExpiry: options.expiry }
              : valueToStore;
            storage.setItem(key, JSON.stringify(payload));
          }

          return valueToStore;
        });
      } catch (error) {
        logError?.(error);
      }
    },
    [key, logError, storage],
  );

  const prevInitialValueRef = useRef(initialValue);
  useEffect(() => {
    if (updateInitialValueOnChange) {
      setStoredValue((prev) => {
        if (prev === prevInitialValueRef.current && initialValue !== prevInitialValueRef.current) {
          prevInitialValueRef.current = initialValue;
          return initialValue;
        }

        prevInitialValueRef.current = prev;
        return prev;
      });
    }
  }, [updateInitialValueOnChange, initialValue]);

  const clearValue = useCallback(() => {
    storage.removeItem(key);
    setStoredValue(initialValue);
  }, [storage, key, initialValue]);

  useEffect(() => {
    if (!poll) {
      return undefined;
    }

    const pollingFn = () => {
      const item = storage.getItem(key);
      const parsedItem = item ? JSON.parse(item) : null;

      if (parsedItem === null) {
        return;
      }

      if (
        parsedItem &&
        typeof parsedItem === 'object' &&
        'meterExpiry' in parsedItem &&
        typeof parsedItem.meterExpiry === 'number' &&
        parsedItem.meterExpiry < Date.now()
      ) {
        storage.removeItem(key);
        setStoredValue(initialValue);
        return;
      }

      const parsedData =
        parsedItem && typeof parsedItem === 'object' && 'meterData' in parsedItem
          ? parsedItem.meterData
          : parsedItem;

      if (JSON.stringify(storedValue) !== JSON.stringify(parsedData)) {
        setStoredValue(parsedData);
      }
    };

    const handle = setInterval(pollingFn, poll);

    return () => {
      clearTimeout(handle);
    };
  }, [poll, storedValue, key, storage, initialValue]);

  return useMemo(() => [storedValue, setValue, clearValue], [storedValue, setValue, clearValue]);
}
